import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartCtx = createContext(null);
const KEY = "os_cart";

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem(KEY) || "[]"));
  const [open, setOpen] = useState(false);
  const merged = useRef(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (!user || user.role !== "customer") {
      merged.current = false;
      return;
    }
    if (merged.current) {
      api.put("/cart", { items: items.map(({ product_id, qty }) => ({ product_id, qty })) }).catch(() => {});
      return;
    }
    merged.current = true;
    api.get("/cart").then(async (r) => {
      if (items.length === 0 && r.data.items.length) {
        const prods = await api.get("/products");
        const map = Object.fromEntries(prods.data.map((p) => [p.id, p]));
        setItems(r.data.items.filter((i) => map[i.product_id]).map((i) => ({ ...map[i.product_id], product_id: i.product_id, qty: i.qty })));
      } else if (items.length) {
        api.put("/cart", { items: items.map(({ product_id, qty }) => ({ product_id, qty })) }).catch(() => {});
      }
    }).catch(() => {});
  }, [user, items]);

  const add = (p, qty = 1) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.product_id === p.id);
      if (ex) return prev.map((i) => (i.product_id === p.id ? { ...i, qty: Math.min(i.qty + qty, p.stock || 999) } : i));
      return [...prev, { ...p, product_id: p.id, qty }];
    });
    toast.success(`${p.name} sepete eklendi`);
    setOpen(true);
  };
  const setQty = (id, qty) => setItems((prev) => prev.map((i) => (i.product_id === id ? { ...i, qty: Math.max(1, qty) } : i)));
  const remove = (id) => setItems((prev) => prev.filter((i) => i.product_id !== id));
  const clear = () => setItems([]);

  const unit = (i) => (i.bulk_price && i.qty >= i.bulk_min_qty ? i.bulk_price : i.sale_price);
  const subtotal = items.reduce((s, i) => s + unit(i) * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, clear, subtotal, count, unit, open, setOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
